import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

/* ============================================================
   Staff Payslip — A4 portrait, one page per payslip.
   Called from staff-payroll.js. Each slip is an employee_payslips
   row with `employees` and `employee_payslip_lines` embedded.
============================================================ */

const A4   = [595.28, 841.89];
const M    = 42;
const MID  = 305;
const MONTHS = ['January','February','March','April','May','June','July',
                'August','September','October','November','December'];

const ORANGE = rgb(0.976, 0.451, 0.086);   // --orange #F97316
const INK    = rgb(0.1, 0.1, 0.1);
const GREY   = rgb(0.4, 0.4, 0.4);
const RULE   = rgb(0.8, 0.8, 0.8);
const TINT   = rgb(1, 0.945, 0.902);       // --orange-light #FFF1E6

/* ── Public API ─────────────────────────────────────────────── */
export async function buildStaffPayslipsPdf(slips, org) {
  const pdf   = await PDFDocument.create();
  const fonts = {
    reg:  await pdf.embedFont(StandardFonts.Helvetica),
    bold: await pdf.embedFont(StandardFonts.HelveticaBold),
  };

  let logo = null;
  try {
    const resp = await fetch('/londmarx%20logo.png');
    if (resp.ok) logo = await pdf.embedPng(await resp.arrayBuffer());
  } catch { /* text fallback */ }

  for (const s of slips) drawPayslip(pdf.addPage(A4), fonts, logo, s, org);
  return pdf.save();
}

export function downloadPdf(bytes, filename) {
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a   = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export function payslipFilename(s) {
  const e = s.employees || {};
  return `payslip-${e.employee_number || s.id}-${s.period_year}-${String(s.period_month).padStart(2, '0')}.pdf`;
}

/* ── One page ───────────────────────────────────────────────── */
function drawPayslip(page, f, logo, s, org) {
  const [W, H] = A4;
  const e      = s.employees || {};
  const lines  = [...(s.employee_payslip_lines || [])]
    .sort((a, b) => (a.sort_order - b.sort_order) || String(a.created_at).localeCompare(String(b.created_at)));
  const periodEnd = new Date(s.period_year, s.period_month, 0);
  const periodLbl = `${MONTHS[s.period_month - 1]} ${s.period_year}`;
  const txt = (t, x, y, size = 9, font = f.reg, color = INK) =>
    page.drawText(String(t ?? ''), { x, y, size, font, color });
  const right = (t, xr, y, size = 9, font = f.reg, color = INK) =>
    txt(t, xr - font.widthOfTextAtSize(String(t), size), y, size, font, color);

  let y = H - M;

  /* header */
  if (logo) {
    const d = logo.scaleToFit(120, 46);
    page.drawImage(logo, { x: M, y: y - d.height, width: d.width, height: d.height });
  } else {
    txt('LONDMARX', M, y - 20, 16, f.bold, ORANGE);
  }
  right('PAYSLIP', W - M, y - 16, 20, f.bold);
  right(periodLbl, W - M, y - 34, 11, f.reg, GREY);

  y -= 60;
  page.drawRectangle({ x: M, y, width: W - 2 * M, height: 2, color: ORANGE });

  /* employer (left) + employee (right) */
  y -= 20;
  txt('EMPLOYER', M, y, 8, f.bold, GREY);
  txt('EMPLOYEE', MID, y, 8, f.bold, GREY);

  const employer = [
    org.company_name || '',
    org.company_registration ? `Reg. No: ${org.company_registration}` : '',
    ...String(org.company_address || '').split(/\n|,/).map(l => l.trim()).filter(Boolean).slice(0, 4),
    org.paye_reference_number ? `PAYE Ref: ${org.paye_reference_number}` : '',
    org.uif_reference_number  ? `UIF Ref: ${org.uif_reference_number}`   : '',
  ].filter(Boolean);

  const employee = [
    ['Name',          `${e.first_name || ''} ${e.surname || ''}`.trim()],
    ['Employee No',   e.employee_number],
    ['ID Number',     e.national_id || 'Outstanding'],
    ['Occupation',    e.occupation || '-'],
    ['Pay Period',    `${fmtDate(new Date(s.period_year, s.period_month - 1, 1))} - ${fmtDate(periodEnd)}`],
    ['Tax Year',      `${s.tax_year - 1}/${String(s.tax_year).slice(2)}`],
    ['Payment',       [e.payment_method, e.bank_name, maskAcc(e.bank_account_number)].filter(Boolean).join(' - ')],
  ];

  let yl = y - 15;
  employer.forEach((l, i) => { txt(l, M, yl, i === 0 ? 10 : 9, i === 0 ? f.bold : f.reg); yl -= 13; });

  let yr = y - 15;
  for (const [label, val] of employee) {
    txt(label, MID, yr, 8.5, f.bold, GREY);
    txt(val || '-', MID + 72, yr, 9);
    yr -= 13;
  }

  y = Math.min(yl, yr) - 10;
  rule(page, y);

  /* earnings */
  const earnings = [
    ['Basic Salary', s.basic_salary],
    ...lines.filter(l => l.line_type === 'earning')
            .map(l => [l.is_taxable ? l.description : `${l.description} (non-taxable)`, l.amount]),
  ];
  y = table(page, f, y - 20, 'EARNINGS', earnings, 'Total Earnings', s.total_earnings, txt, right);

  /* deductions */
  const deductions = [
    ['PAYE (Income Tax)', s.paye],
    ['UIF (1%)',          s.uif_employee],
    ...lines.filter(l => l.line_type === 'deduction').map(l => [l.description, l.amount]),
  ];
  y = table(page, f, y - 32, 'DEDUCTIONS', deductions, 'Total Deductions', s.total_deductions, txt, right);

  /* nett pay band */
  y -= 30;
  page.drawRectangle({ x: M, y: y - 8, width: W - 2 * M, height: 28, color: TINT });
  page.drawRectangle({ x: M, y: y - 8, width: 3, height: 28, color: ORANGE });
  txt('NETT PAY', M + 14, y + 2, 12, f.bold);
  right(rands(s.net_pay), W - M - 12, y + 2, 13, f.bold);

  /* employer contributions — informational */
  y -= 42;
  txt('EMPLOYER CONTRIBUTIONS (not deducted from pay)', M, y, 8, f.bold, GREY);
  y -= 14;
  txt('UIF - Employer (1%)', M, y, 8.5, f.reg, GREY);
  right(rands(s.uif_employer), W - M, y, 8.5, f.reg, GREY);
  y -= 12;
  txt('Skills Development Levy (1%)', M, y, 8.5, f.reg, GREY);
  right(rands(s.sdl), W - M, y, 8.5, f.reg, GREY);
}

/* earnings / deductions block; returns new y */
function table(page, f, y, title, rows, totalLabel, total, txt, right) {
  const [W] = A4;
  txt(title, M, y, 10, f.bold);
  right('Amount', W - M, y, 8, f.bold, GREY);
  y -= 6;
  rule(page, y);
  for (const [desc, amt] of rows) {
    y -= 15;
    txt(desc, M, y, 9);
    right(rands(amt), W - M, y, 9);
  }
  y -= 8;
  rule(page, y, 0.4);
  y -= 14;
  right(totalLabel, W - M - 110, y, 9.5, f.bold);
  right(rands(total), W - M, y, 9.5, f.bold);
  return y;
}

/* ── helpers ────────────────────────────────────────────────── */
function rule(page, y, thickness = 0.6) {
  page.drawLine({ start: { x: M, y }, end: { x: A4[0] - M, y }, thickness, color: RULE });
}

function rands(n) {
  return 'R ' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(d) {
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function maskAcc(acc) {
  if (!acc) return '';
  const s = String(acc).replace(/\s/g, '');
  return s.length > 4 ? `****${s.slice(-4)}` : s;
}
